import type { MemoBlock, MemoEntry } from "@/lib/types";
import { orderMemoLogEntries } from "@/lib/memoLogOrder";

/**
 * One Abschnitt-level group in a Protokoll: a Zwischenüberschrift and the points
 * beneath it, the run of points before the first Zwischenüberschrift, or a whole
 * block that has none.
 *
 * 🔴 `heading` is a PROPERTY of the section, deliberately NOT a member of
 * `entries`. Before this existed, the subheading was an ordinary entry inside the
 * section's own list, so the collapsed filter (`!e.retired`) removed a RETIRED
 * heading from its own section — the section rendered headless and its reveal
 * control docked under the section above it. Keeping the heading out of `entries`
 * makes that bug unrepresentable rather than guarded against.
 */
export interface MemoSection {
  /** Subheading entry id, else the block id. Matches FocusUnit.anchorId. */
  anchorId: string;
  /** The Zwischenüberschrift, or null for a leading run / heading-less block. */
  heading: MemoEntry | null;
  /** Points and updates in render order. NEVER contains `heading`. */
  entries: MemoEntry[];
  hasClosedHere: boolean;
  /** Closed POINTS in this section. A closed heading is a property of the
   *  section, not one of its closed items, so it is never counted here. */
  closedCount: number;
  /** Denormalised backend signal: closures in PRIOR meetings for this section. */
  historyCount: number;
}

/**
 * Split a block into sections in render order.
 *
 * Uses the SAME ordering the block renders with (`orderMemoLogEntries` —
 * chronological, updates welded to their to-do). Idempotent, so it does not
 * matter who applies it first.
 */
export function buildMemoSections(block: MemoBlock): MemoSection[] {
  const ordered = orderMemoLogEntries(block.entries);
  const sections: MemoSection[] = [];
  let current: MemoSection | null = null;

  const open = (heading: MemoEntry | null): MemoSection => {
    const s: MemoSection = {
      anchorId: heading ? heading.id : block.id,
      heading,
      entries: [],
      hasClosedHere: false,
      closedCount: 0,
      historyCount: (heading ? heading.history_count : block.history_count) ?? 0,
    };
    sections.push(s);
    return s;
  };

  for (const e of ordered) {
    if (e.kind === "subheading") {
      current = open(e);
      continue;
    }
    if (current === null) current = open(null);
    current.entries.push(e);
    // Updates are board activity welded to a to-do; they are never retired and
    // must not inflate the closed count.
    if (e.kind !== "update" && e.retired) current.closedCount += 1;
  }

  // An empty block stays reachable so the presentation cursor can land on it and
  // the user can append.
  if (sections.length === 0) open(null);

  for (const s of sections) s.hasClosedHere = s.closedCount > 0;
  return sections;
}

export interface VisibilityState {
  expanded: boolean;
  presenting: boolean;
  finalized: boolean;
  /** Points retired during THIS presentation — they stay on screen so the room
   *  watches them close, instead of vanishing mid-sentence. */
  retiredThisSession: ReadonlySet<string>;
}

/**
 * The entries a section shows right now.
 *
 * 🔴 The update rule is the structural half: an update (board activity) is welded
 * to the to-do it reports on. Filtering per-entry left it behind when its parent
 * collapsed — an attributed line with no visible subject, floating under the
 * previous section. It hides WITH its parent.
 *
 * 🔴 ORDER-INDEPENDENT, in TWO passes. The single-pass version asked "is my
 * parent in the kept set YET", a set only filled as the loop advanced — so an
 * update that happened to sit BEFORE its parent was dropped even though the
 * parent was present and visible. Membership, never position: pass 1 computes
 * which non-update entries survive, pass 2 emits.
 *
 * Fails OPEN — an update is kept whenever its parent cannot be resolved to a
 * to-do in THIS section (no `parent_entry_id`, or one naming something this
 * section does not hold). Only a resolvable parent that did NOT survive hides
 * its update. Losing a colleague's board comment is worse than showing one line
 * too many.
 */
export function visibleEntries(section: MemoSection, v: VisibilityState): MemoEntry[] {
  if (v.expanded) return section.entries;

  const survives = (e: MemoEntry): boolean =>
    !e.retired || (v.presenting && !v.finalized && v.retiredThisSession.has(e.id));

  // Pass 1: every resolvable parent this section holds (a parent is always a
  // to-do, never another update) and which of them stay on screen.
  const parentIds = new Set<string>();
  const survivingIds = new Set<string>();
  for (const e of section.entries) {
    if (e.kind === "update") continue;
    parentIds.add(e.id);
    if (survives(e)) survivingIds.add(e.id);
  }

  // Pass 2: emit in render order.
  return section.entries.filter((e) => {
    if (e.kind !== "update") return survives(e);
    const parentId = e.parent_entry_id;
    if (!parentId || !parentIds.has(parentId)) return true; // fail open
    return survivingIds.has(parentId);
  });
}
